import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Typography, Space, Button, Input, Select, DatePicker, Spin, message } from 'antd';
import { UserOutlined, ShoppingOutlined, FileTextOutlined, DollarOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { adminService } from '../../services/adminService';
import { downloadBlob, readBlobError } from '../../utils/fileDownload';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface DashboardData {
  summary: {
    totalUsers: number;
    totalProducts: number;
    totalQuotations: number;
  };
  userStats: Record<string, number>;
  quotationStats: Record<string, number>;
  recentQuotations: Array<{
    RefNo: string;
    TraderName: string;
    webEmail: string;
    DateIssued: string;
    TotalDueAmt: number;
    webStatus: string;
    webUserID: string;
  }>;
}

const AdminDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const { user } = useAuthStore();
  const navigate = useNavigate();

  // Export the recent-orders list shown on this card to CSV, through the same
  // endpoint the Orders screen uses so both files have identical columns.
  // The limit matches getRecentQuotations(10) behind this card, and the default
  // ordering is the same (webDateEntry DESC), so the file mirrors the rows shown.
  const handleExportRecent = async () => {
    if ((dashboardData?.recentQuotations ?? []).length === 0) {
      message.info('No recent orders to export.');
      return;
    }
    try {
      const blob = await adminService.exportOrdersCSV({ limit: 10 });
      downloadBlob(blob, `recent-orders-${dayjs().format('YYYYMMDD-HHmmss')}.csv`);
      message.success('Export completed');
    } catch (error) {
      console.error('Failed to export recent orders:', error);
      message.error(await readBlobError(error, 'Failed to export recent orders'));
    }
  };

  useEffect(() => {
    if (user?.type === 'ADMIN') {
      loadDashboardData();
    }
  }, [user]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const data = await adminService.getDashboardAnalytics();
      setDashboardData(data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      'ORDER': 'blue',
      'PROCESSING': 'orange',
      'CONFIRMED': 'green',
      'SHIPPED': 'cyan',
      'COMPLETED': 'purple',
      'CANCELLED': 'red'
    };
    return statusColors[status] || 'default';
  };

  const recentQuotationsColumns = [
    {
      title: 'Order No.',
      dataIndex: 'RefNo',
      key: 'RefNo',
      render: (text: string) => <Text strong>{text}</Text>
    },
    {
      title: 'Customer',
      dataIndex: 'TraderName',
      key: 'TraderName'
    },
    {
      title: 'Email',
      dataIndex: 'webEmail',
      key: 'webEmail'
    },
    {
      title: 'Date',
      dataIndex: 'DateIssued',
      key: 'DateIssued',
      render: (date: string) => dayjs(date).format('MMM D, YYYY')
    },
    {
      title: 'Amount',
      dataIndex: 'TotalDueAmt',
      key: 'TotalDueAmt',
      render: (amount: number) => `$${amount.toFixed(2)}`
    },
    {
      title: 'Status',
      dataIndex: 'webStatus',
      key: 'webStatus',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{status}</Tag>
      )
    }
  ];

  // Note: Admin access is enforced by AuthGuard at the route level

  if (loading) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <Title level={3}>Failed to Load Dashboard</Title>
        <Button type="primary" onClick={loadDashboardData}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Admin Dashboard</Title>
        <Text type="secondary">Overview of system statistics and recent activity</Text>
      </div>

      {/* Summary Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Users"
              value={dashboardData.summary.totalUsers}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#4d8b55' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Products"
              value={dashboardData.summary.totalProducts}
              prefix={<ShoppingOutlined />}
              valueStyle={{ color: '#ffae00' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Orders"
              value={dashboardData.summary.totalQuotations}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#4d8b55' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Revenue"
              value={dashboardData.recentQuotations.reduce((sum, q) => sum + q.TotalDueAmt, 0)}
              prefix={<DollarOutlined />}
              precision={2}
              valueStyle={{ color: '#ffae00' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {/* User Statistics */}
        <Col xs={24} lg={12}>
          <Card title="User Statistics by Type" extra={<Button size="small">View All</Button>}>
            <div style={{ padding: '16px 0' }}>
              {Object.entries(dashboardData.userStats).map(([type, count]) => (
                <div key={type} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <Text>{type}</Text>
                  <Text strong>{count}</Text>
                </div>
              ))}
            </div>
          </Card>
        </Col>

        {/* Quotation Statistics */}
        <Col xs={24} lg={12}>
          <Card title="Order Statistics by Status" extra={<Button size="small">View All</Button>}>
            <div style={{ padding: '16px 0' }}>
              {Object.entries(dashboardData.quotationStats).map(([status, count]) => (
                <div key={status} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div>
                    <Tag color={getStatusColor(status)}>{status}</Tag>
                  </div>
                  <Text strong>{count}</Text>
                </div>
              ))}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Recent Quotations */}
      <Card 
        title="Recent Orders"
        style={{ marginTop: '24px' }}
        extra={
          <Space>
            <Button size="small" onClick={handleExportRecent}>Export</Button>
            <Button type="primary" size="small" onClick={() => navigate('/admin/quotations')}>
              View All Orders
            </Button>
          </Space>
        }
      >
        <Table
          columns={recentQuotationsColumns}
          dataSource={dashboardData.recentQuotations}
          rowKey="RefNo"
          pagination={{
            pageSize: 10,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            showSizeChanger: false
          }}
          scroll={{ x: 800 }}
        />
      </Card>
    </div>
  );
};

export default AdminDashboard;